import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { isDevelopment } from '../constants';

const DEFAULT_DWELL_MS = 90_000;

const TAG_REGISTRATION_STEP = 'replay.registration_step';
const TAG_PERSONAL_DATA = 'replay.personal_data';
const TAG_TRIGGER = 'replay.trigger';

interface UseSentryReplayOnDwellOptions {
  enabled?: boolean;
  stepTag: string;
  dwellMs?: number;
  captureIssue?: boolean;
}

const clearReplayTags = (): void => {
  Sentry.setTag(TAG_REGISTRATION_STEP, undefined);
  Sentry.setTag(TAG_PERSONAL_DATA, undefined);
  Sentry.setTag(TAG_TRIGGER, undefined);
};

export const useSentryReplayOnDwell = ({
  enabled = true,
  stepTag,
  dwellMs = DEFAULT_DWELL_MS,
  captureIssue = false,
}: UseSentryReplayOnDwellOptions): void => {
  const isRecordingRef = useRef(false);

  useEffect(() => {
    if (!enabled || isDevelopment || !process.env.REACT_APP_SENTRY_DSN) {
      return;
    }
    const timerId = window.setTimeout(() => {
      const replay = Sentry.getReplay();
      if (!replay) {
        return;
      }

      const trigger = `dwell_${Math.round(dwellMs / 1000)}s`;

      Sentry.setTag(TAG_REGISTRATION_STEP, stepTag);
      Sentry.setTag(TAG_PERSONAL_DATA, 'true');
      Sentry.setTag(TAG_TRIGGER, trigger);

      if (replay.getReplayId()) {
        void replay.flush();
      } else {
        replay.start();
      }

      isRecordingRef.current = true;

      if (captureIssue) {
        Sentry.captureMessage(`Registration step dwell: ${stepTag} (${trigger})`, {
          level: 'warning',
          fingerprint: ['registration-step-dwell', stepTag],
        });
      }
    }, dwellMs);

    return () => {
      window.clearTimeout(timerId);

      if (!isRecordingRef.current) {
        return;
      }

      const replay = Sentry.getReplay();

      if (replay) {
        void replay.stop();
      }

      clearReplayTags();
      isRecordingRef.current = false;
    };
  }, [enabled, stepTag, dwellMs, captureIssue]);
};
